import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Droplets, Mail, MapPinned, Phone, Cookie } from 'lucide-react';

/**
 * Footer — rodapé principal (extraído do Home).
 *
 * @typedef {Object} FooterColumn
 * @property {string} title Título da coluna.
 * @property {Array<[string,string]>} links Pares [label, to].
 *
 * @typedef {Object} FooterProps
 * @property {string} [brandName='Águas Vivas'] Nome da marca.
 * @property {string} [brandSubtitle='Caratinga · MG'] Subtítulo.
 * @property {string} [description] Texto institucional.
 * @property {string} [email] E-mail de contato.
 * @property {string} [address] Endereço.
 * @property {string} [phone] Telefone.
 * @property {Array<FooterColumn>} [columns] Colunas de links.
 */

export default function Footer({
  brandName = 'Comunidade Águas Vivas',
  brandSubtitle = 'Caratinga · MG',
  description = 'Uma comunidade que existe para pregar o Evangelho, fazer discípulos e transformar vidas em Caratinga.',
  email = 'contato@aguasvivas.com.br',
  address = 'Rua Coronel Pedro Cordeiro, 210 · Centro · Caratinga/MG',
  phone = '(33) 3321-0000',
  columns = [],
}) {
  const [emailValue, setEmailValue] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (emailValue.trim()) {
      setSubscribed(true);
      setEmailValue('');
    }
  };

  return (
    <footer className="mt-12 bg-slate-900 text-slate-300">
      <div className="app-container py-12">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Col 1: Logo + Newsletter */}
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white">
                <Droplets size={22} />
              </span>
              <div className="leading-tight">
                <p className="font-heading text-base font-extrabold text-white">{brandName}</p>
                <p className="text-[11px] text-slate-400">{brandSubtitle}</p>
              </div>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">{description}</p>
            <form onSubmit={handleSubscribe} className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Newsletter</p>
              <div className="mt-2 flex overflow-hidden rounded-xl bg-slate-800">
                <input
                  type="email"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  placeholder="Seu e-mail"
                  aria-label="Seu e-mail"
                  className="w-full bg-transparent px-3 py-2.5 text-sm text-white outline-none placeholder:text-slate-500"
                />
                <button
                  type="submit"
                  className="shrink-0 bg-primary px-4 text-sm font-bold text-white transition-colors hover:bg-primary-dark"
                >
                  {subscribed ? '✓' : 'Assinar'}
                </button>
              </div>
              {subscribed && (
                <p className="mt-2 text-xs text-emerald-400">Inscrição realizada com sucesso!</p>
              )}
            </form>
          </div>

          {/* Colunas de links */}
          {columns.map((col) => (
            <div key={col.title}>
              <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-white">{col.title}</h4>
              <ul className="mt-4 space-y-2.5 text-sm">
                {col.links.map(([label, to]) => (
                  <li key={label}>
                    <Link to={to} className="text-slate-400 transition-colors hover:text-white">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Col Contato */}
          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-wider text-white">Contato</h4>
            <ul className="mt-4 space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-2.5">
                <Mail size={16} className="mt-0.5 shrink-0 text-primary" />
                {email}
              </li>
              <li className="flex items-start gap-2.5">
                <MapPinned size={16} className="mt-0.5 shrink-0 text-primary" />
                {address}
              </li>
              <li className="flex items-start gap-2.5">
                <Phone size={16} className="mt-0.5 shrink-0 text-primary" />
                {phone}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Copyright bar */}
      <div className="border-t border-slate-800">
        <div className="app-container flex flex-col items-center justify-between gap-3 py-5 text-xs text-slate-500 md:flex-row">
          <p>© {new Date().getFullYear()} Comunidade Águas Vivas Caratinga. Todos os direitos reservados.</p>
          <p className="flex items-center gap-1.5">
            <Cookie size={14} /> Utilizamos cookies conforme a LGPD. Ao continuar, você concorda com nossa política.
          </p>
        </div>
      </div>
    </footer>
  );
}
